import React, { useState, forwardRef } from 'react';
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { ChevronDown } from 'lucide-react';
import { WIND_HEIGHTS } from '../../../utils/windChartConstants';
import { ChartEmptyState } from '../ChartEmptyState';

export interface DistributionBin {
  min: number;
  max: number;
  percentage: number;
}

export interface AreaStats {
  min: number;
  max: number;
  mean: number;
  std_dev: number;
}

interface WindDistributionChartProps {
  distribution: DistributionBin[];
  stats: AreaStats | null;
  selectedHeight: string;
  onHeightChange?: (height: string) => void;
}

export const WindDistributionChart = forwardRef<HTMLDivElement, WindDistributionChartProps>(
  ({ distribution, stats, selectedHeight, onHeightChange }, ref) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    if (!distribution || distribution.length === 0) {
      return (
        <ChartEmptyState
          ref={ref}
          message="Sem dados disponíveis para esta seleção."
        />
      );
    }

    const root = typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null;
    const primaryColor = root?.getPropertyValue('--chart-2').trim() || "#4A90D9";
    const textSecondary = root?.getPropertyValue('--color-gray-light').trim() || "#61656d";
    const gridLineColor = root?.getPropertyValue('--color-gray-pale').trim() || "#d9d9d9";
    const axisLineColor = root?.getPropertyValue('--border').trim() || "#d9d9d9";
    const textPrimary = root?.getPropertyValue('--foreground').trim() || "#000000";
    const backgroundColor = root?.getPropertyValue('--background').trim() || "#ffffff";
    const borderColor = root?.getPropertyValue('--border').trim() || "#d9d9d9";

    const categories = distribution.map((bin, index) => {
      if (index === 0) return `< ${bin.max.toFixed(1)}`;
      if (index === distribution.length - 1) return `> ${bin.min.toFixed(1)}`;
      return `${bin.min.toFixed(1)} - ${bin.max.toFixed(1)}`;
    });

    const chartColor = "#227093";
    const percentages = distribution.map((bin) => ({ y: bin.percentage, color: chartColor }));

    const options: Highcharts.Options = {
      chart: { type: 'column', height: 300, backgroundColor: 'transparent', marginTop: 40, marginLeft: 60, marginRight: 20, spacingBottom: 5 },
      title: { text: undefined },
      legend: { enabled: false },
      credits: { enabled: false },
      exporting: { enabled: false },
      xAxis: {
        categories,
        crosshair: true,
        labels: { style: { fontSize: "11px", color: textSecondary } },
        lineColor: axisLineColor,
        tickColor: 'transparent',
        title: {
          text: 'm/s',
          margin: 10,
          style: { fontSize: "11px", color: textSecondary, fontWeight: "normal" }
        }
      },
      yAxis: {
        min: 0,
        max: 100,
        tickInterval: 20,
        title: {
          text: "Área",
          margin: 10,
          style: { fontSize: "11px", color: textSecondary, fontWeight: "normal" },
        },
        labels: {
          format: '{value}%',
          style: { fontSize: "11px", color: textSecondary }
        },
        gridLineColor: gridLineColor,
      },
      tooltip: {
        backgroundColor, borderColor, borderRadius: 10, padding: 10, shadow: true, useHTML: true,
        headerFormat: `<div style="font-size: 11px; color: ${textSecondary}; margin-bottom: 6px;">Faixa: {point.key} m/s</div>`,
        pointFormat: `<div style="font-weight: bold; color: ${textPrimary}; font-size: 12px;"><span style="color: ${primaryColor};">●</span> {point.y:.1f}% da Área</div>`,
      },
      plotOptions: {
        column: { pointPadding: 0.02, groupPadding: 0.05, borderWidth: 0, borderRadius: 2 },
      },
      series: [{ type: "column", name: "Distribuição", data: percentages }],
    };

    return (
      <div ref={ref} className="relative pb-4 bg-white rounded-lg shadow-md">
        <div className="absolute top-0 right-0 z-10">
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(prev => !prev)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-[12px] font-medium text-[#64748B] hover:text-[#0C2856] transition-colors"
            >
              Altura {selectedHeight}
              <ChevronDown size={14} className={`transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 mt-1 w-[110px] bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                {WIND_HEIGHTS.map((h) => (
                  <button key={h}
                    onClick={() => { onHeightChange?.(h); setDropdownOpen(false); }}
                    className={`block w-full px-4 py-2 text-left text-[12px] transition-colors
                      ${selectedHeight === h ? "bg-gray-50 text-[#0C2856] font-bold" : "text-gray-600 hover:bg-gray-50 hover:text-[#0C2856]"}`}
                  >
                    {h}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="w-full">
          <HighchartsReact highcharts={Highcharts} options={options} />
        </div>

        {stats && (
          <div className="flex justify-end items-center gap-8 mt-4 pr-4">
            <StatItem label="Média de Velocidade" value={`${stats.mean.toFixed(2)} m/s`} />
            <StatItem label="Valor Mínimo" value={`${stats.min.toFixed(2)} m/s`} />
            <StatItem label="Valor Máximo" value={`${stats.max.toFixed(2)} m/s`} />
          </div>
        )}
      </div>
    );
  }
);

WindDistributionChart.displayName = 'WindDistributionChart';

const StatItem = ({ label, value }: { label: string, value: string }) => (
  <div className="flex flex-col items-center">
    <span className="text-[12px] text-[#0C2856] font-medium mb-1">{label}</span>
    <span className="text-[14px] font-bold text-[#1E293B]">{value}</span>
  </div>
);
